import { motion, useReducedMotion } from "framer-motion";
import { Reveal, MaskedHeading } from "../Reveal";
import { IMAGES } from "../../data/content";

const Para = ({ children }) => (
  <Reveal className="text-pearl/75 text-lg leading-relaxed max-w-xl mb-5">{children}</Reveal>
);

const ParallaxImg = ({ src, alt, className }) => {
  const reduced = useReducedMotion();
  return (
    <motion.div
      className={`overflow-hidden rounded-sm ${className}`}
      initial={reduced ? { opacity: 0 } : { opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
    >
      <img src={src} alt={alt} loading="lazy" className="w-full h-full object-cover" />
    </motion.div>
  );
};

export default function Problem() {
  return (
    <section
      className="section bg-abyss py-24 md:py-40 relative"
      data-testid="problem-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <span className="chapter-num">CHAPITRE 01 — LE SIGNAL</span>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-8">
          <div className="lg:col-span-7">
            <MaskedHeading
              tag="h2"
              className="font-display font-medium text-[clamp(2.3rem,5vw,4.2rem)] leading-[1.05] tracking-tight text-pearl"
              lines={["Tu n'es peut-être", "pas perdu."]}
            />
            <MaskedHeading
              tag="p"
              delay={0.2}
              className="font-display italic text-[clamp(1.6rem,3.5vw,2.8rem)] leading-tight text-aqua mt-4"
              lines={["Tu vis peut-être trop loin", "de ton propre signal."]}
            />
          </div>

          <div className="lg:col-span-5 lg:pt-4">
            <Para>Tu peux savoir beaucoup de choses sur toi.</Para>
            <Para>
              Comprendre tes blessures. Identifier tes schémas. Connaître les mécanismes
              qui t'animent. Avoir lu, appris, médité, travaillé, analysé.
            </Para>
            <Para>Et pourtant sentir qu'une partie de toi reste à distance.</Para>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mt-16 md:mt-24 items-center">
          <ParallaxImg
            src={IMAGES.immersion}
            alt="Adulte nageant calmement sous la surface d'une eau claire"
            className="lg:col-span-5 aspect-[3/4]"
          />
          <div className="lg:col-span-7">
            <Para>
              Comme si ta vie avançait, mais que quelque chose d'essentiel n'avançait plus
              avec elle.
            </Para>
            <Para>Ce n'est pas toujours une crise. Parfois, c'est plus subtil.</Para>
            <Para>Tu fonctionnes. Tu assumes. Tu réponds aux attentes. Tu continues.</Para>
            <Para>
              Mais ton désir devient moins audible. Ton élan se brouille. Ta joie demande
              davantage d'efforts.
            </Para>
            <Para>
              Et la moindre décision semble devoir traverser un conseil d'administration
              installé dans ta tête.
            </Para>
          </div>
        </div>

        <Reveal delay={0.1} className="mt-16 md:mt-24 max-w-3xl">
          <p className="font-display text-2xl md:text-3xl leading-snug text-pearl">
            La Traversée n'a pas été créée pour t'apprendre une nouvelle théorie sur toi.
            Elle a été créée pour t'aider à revenir au contact de ce qui est déjà vivant.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
